import { Fragment, ReactElement, useCallback, useEffect, useState } from 'react';
import { Box } from '@breathelife/mui';
import { useTranslation } from 'react-i18next';
import { ModalLayout } from '../../Layouts/Modal/ModalLayout';
import { SubmitButton } from '../Button/SubmitButton';
import Typography from '../Typography';
import styled from '../../Styles/themed-styled-components';
import { Button } from '../Button/Button';
import { useSelector, useCarrierContext } from '../../Hooks';

export const Link = styled.a`
  color: ${(props) => props.theme.colors.blue[60]};
`;

const ErrorText = styled(Typography)`
  color: ${(props) => props.theme.colors.red[50]};
`;

const Footer = styled.footer`
  border-top: 1px solid ${(props) => props.theme.colors.grey[30]};
  padding: 16px;
`;
const STORAGE_KEY = 'cookie_consent';

export function CookieConsentModal(): ReactElement {
  const { t } = useTranslation();
  const authentication = useSelector((store) => store.leadPlatform.authentication);
  const { features } = useCarrierContext();
  const cookieConsent = features.cookieConsent;

  const [isOpen, setIsOpen] = useState(false);
  const [didReject, setDidReject] = useState(false);

  const getCookieConsent = useCallback((userId: string | null): boolean => {
    if (!userId) return false;

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;

    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
      return (parsed as Record<string, string>)[userId] === 'true';
    } catch (error) {
      return false;
    }
  }, []);

  const setCookieConsent = useCallback((userId: string | null) => {
    if (!userId) return;

    const raw = window.localStorage.getItem(STORAGE_KEY);
    let consentMap: Record<string, string> = {};

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          consentMap = parsed as Record<string, string>;
        }
      } catch (error) {
        consentMap = {};
      }
    }

    consentMap[userId] = 'true';
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consentMap));
  }, []);

  useEffect(() => {
    try {
      const { isAuthenticated = false, user: { id: userId = null } = {} } = authentication;
      const hasConsent = getCookieConsent(userId);
      setIsOpen(cookieConsent.enabled && !hasConsent && isAuthenticated);
    } catch (error) {
      setIsOpen(false);
    }
  }, [authentication, cookieConsent.enabled, getCookieConsent]);

  const onAccept = useCallback(() => {
    try {
      const userId = authentication?.user?.id ?? null;
      setCookieConsent(userId);
      setIsOpen(false);
      setDidReject(false);
    } catch (error) {
      setIsOpen(false);
    }
  }, [authentication?.user?.id, setCookieConsent]);

  const onReject = useCallback(() => {
    setDidReject(true);
  }, []);

  if (!isOpen) return <Fragment />;

  return (
    <ModalLayout
      maxWidth='sm'
      isOpen={isOpen}
      closeModal={onReject}
      title={t('modals.cookieConsent.title')}
      hideCancel={true}
      closeButton={<Fragment />}
    >
      <Box mb={3}>
        <Typography variant='body1'>{t('modals.cookieConsent.body')}</Typography>
      </Box>
      <Box mb={2}>
        <Link href={t('modals.cookieConsent.linkUrl')} target='_blank' rel='noopener noreferrer'>
          {t('modals.cookieConsent.linkText')}
        </Link>
      </Box>
      <Footer>
        <Box display='flex' width='100%' justifyContent='center' gap={2}>
          <SubmitButton isLoading={false} onClick={onAccept}>
            {t('modals.cookieConsent.acceptButton')}
          </SubmitButton>
          <Button variant='outlined' onClick={onReject}>
            {t('modals.cookieConsent.rejectButton')}
          </Button>
        </Box>
        {didReject && (
          <Box mt={2}>
            <ErrorText component='p' variant='small1'>
              {t('modals.cookieConsent.rejectMessage')}
            </ErrorText>
          </Box>
        )}
      </Footer>
    </ModalLayout>
  );
}
